import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronDown } from "lucide-react";
import { Flag } from "@/components/Flag";
import { cn } from "@/lib/utils";

/**
 * Searchable country dropdown with SVG flags.
 *
 * Props:
 *   options       Array<{ code: string, name: string, dial?: string, flag?: string }>
 *   value         Currently selected value (matches `valueField`)
 *   onChange(v)   Called with the new value
 *   valueField    "code" (default) — use ISO code as value, or "name" to use the country name
 *   placeholder   Text shown when nothing is selected
 *   disabled      Boolean
 *   testId        data-testid on the trigger button
 *   className     Extra classes for the trigger
 *   showDial      When true, renders the country's dial code after the name
 *   compact       When true, trigger shows only the flag + selected code/dial (used inside compact rows)
 *   required      When true and no value, marks the trigger with aria-invalid so native form flows can flag it
 */
export function CountryCombobox({
  options,
  value,
  onChange,
  valueField = "code",
  placeholder = "Select country…",
  disabled = false,
  testId,
  className = "",
  showDial = false,
  compact = false,
  required = false,
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o[valueField] === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          disabled={disabled}
          aria-invalid={required && !value ? true : undefined}
          className={cn(
            "w-full min-h-[42px] flex items-center gap-2 border border-[#e2e8f0] bg-white text-[#0f1b2d] rounded-md px-3 py-2 text-sm outline-none transition-colors",
            "hover:border-[#94a3b8] focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/15",
            "disabled:bg-[#f1f5f9] disabled:text-[#94a3b8] disabled:cursor-not-allowed disabled:hover:border-[#e2e8f0]",
            open && "border-[#2563eb] ring-2 ring-[#2563eb]/15",
            className,
          )}
        >
          {selected ? (
            <>
              <Flag code={selected.code} />
              <span className="flex-1 text-left truncate">
                {compact
                  ? (showDial ? selected.dial : selected.code)
                  : (showDial ? `${selected.name} · ${selected.dial}` : selected.name)}
              </span>
            </>
          ) : (
            <span className="flex-1 text-left text-[#94a3b8] truncate">{placeholder}</span>
          )}
          <ChevronDown className="w-4 h-4 text-[#64748b] flex-shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-[--radix-popover-trigger-width] min-w-[240px] p-0 max-w-[95vw]"
        data-testid={testId ? `${testId}-popover` : undefined}
      >
        <Command
          // Custom filter so users can also search by ISO code or dial code
          filter={(itemValue, search) => {
            const q = search.toLowerCase();
            return itemValue.toLowerCase().includes(q) ? 1 : 0;
          }}
        >
          <CommandInput
            placeholder="Search country…"
            data-testid={testId ? `${testId}-search` : undefined}
          />
          <CommandList className="max-h-[280px]">
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => {
                const val = opt[valueField];
                // The searchable string carries name + code + dial for flexible lookup
                const searchStr = [opt.name, opt.code, opt.dial].filter(Boolean).join(" ");
                return (
                  <CommandItem
                    key={opt.code}
                    value={searchStr}
                    onSelect={() => {
                      onChange(val);
                      setOpen(false);
                    }}
                    data-testid={testId ? `${testId}-option-${opt.code}` : undefined}
                    className="flex items-center gap-2.5 cursor-pointer"
                  >
                    <Flag code={opt.code} />
                    <span className="flex-1 truncate">{opt.name}</span>
                    {showDial && opt.dial && (
                      <span className="text-xs text-[#64748b] font-medium">{opt.dial}</span>
                    )}
                    <Check
                      className={cn(
                        "w-4 h-4 text-[#2563eb]",
                        value === val ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default CountryCombobox;
